<template>
  <div v-if="isDataLoaded" class="d-flex align-items-center">
    <div class="me-4">
      <Popper hover arrow>
        <OpeningStatusIcon :status-color="statusColor" />
        <template #content>
          <div class="text-start" v-dompurify-html="statusMessage"></div>
        </template>
      </Popper>
    </div>
    <div
      v-if="Object.keys(departmentOpeningHours.openingHours).length"
      ref="schedulesRef"
      class="align-self-baseline"
      style="cursor: pointer"
    >
      <div
        class="show-schedules-container"
        :class="showScheduleHover ? 'show-schedules-container--hover' : null"
        @mouseover="showScheduleHover = true"
        @mouseleave="showScheduleHover = false"
      >
        <TransitionGroup>
          <Popper
            arrow
            class="popper-schedules"
            :disable-click-away="false"
            :show="showSchedulePopper"
            key="icon"
          >
            <AppIcon icon="schedule" @click="showSchedulePopper = !showSchedulePopper" />
            <template #content>
              <DepartmentsSchedules
                :lang="lang"
                :department-opening-hours="departmentOpeningHours.openingHours"
              />
            </template>
          </Popper>
          <div
            v-if="showScheduleHover"
            @click="showSchedulePopper = !showSchedulePopper"
            key="text"
            class="d-none d-md-block show-schedules my-auto"
          >
            {{ $t('header.opening_hours.show') }}
          </div>
        </TransitionGroup>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, reactive, onMounted, onUnmounted, computed } from 'vue'
import { onClickOutside } from '@vueuse/core'
import axios, { AxiosError } from 'axios'
import OpeningStatusIcon from '~conciergeUiCommons/components/OpeningStatusIcon.vue'
import { useDepartmentOpeningHours } from '~conciergeUiCommons/store/departmentsOpeningHoursStore'
import DepartmentsSchedules from '~conciergeUiCommons/components/DepartmentsSchedules.vue'
import { trans, wTrans } from 'laravel-vue-i18n'
import { useSubmissionFormStore } from '~conciergeUiCommons/store/submissionFormStore'

let intervalId: string | number | NodeJS.Timeout | undefined
const showSchedulePopper = ref<boolean>(false)
const showScheduleHover = ref<boolean>(false)
const schedulesRef = ref(null)
onClickOutside(schedulesRef, () => (showSchedulePopper.value = false))

const BASE_API_URL = '/api/alfred/opening-hours'
const departmentOpeningHours = useDepartmentOpeningHours()
const departmentStatus = ref<boolean>(false)
const open = ref<boolean>(false)
const currentOpenRange = reactive<Record<string, string>>({
  start: '',
  end: ''
})
const isAboutToOpen = ref<boolean>(false)
const isOpenedIn = ref<string>('')
const isAboutToClose = ref<boolean>(false)
const isClosedIn = ref<string>('')
const closingDetails = ref<string>('')
const isDataLoaded = ref<boolean>(false)
const lang = computed(() => {
  return useSubmissionFormStore().formLanguage
})
const fetchDepartmentStatus = async (): Promise<void> => {
  try {
    const response = await axios.get(`${BASE_API_URL}/status?lang=${lang.value}`)
    departmentStatus.value = response.data.open
    open.value = response.data.open
    currentOpenRange.start = response.data.currentOpenRange.start
    currentOpenRange.end = response.data.currentOpenRange.end
    isAboutToOpen.value = response.data.isAboutToOpen
    isOpenedIn.value = response.data.isOpenedIn
    isAboutToClose.value = response.data.isAboutToClose
    isClosedIn.value = response.data.isClosedIn
    closingDetails.value = response.data.closingDetails
    isDataLoaded.value = true
  } catch (error: unknown | Error | AxiosError) {
    isDataLoaded.value = false
    if (axios.isAxiosError(error) && error.response) {
      console.error(
        'Error when fetching department status:' + error.response.status,
        error.response.statusText
      )
      throw error.response
    }
    if (error instanceof Error) {
      console.error('Error when fetching department status:', error.message)
    } else {
      console.error('Error when fetching department status:', error)
    }
    throw error
  }
}

const statusColor = computed(() => {
  if (!open.value && !isAboutToOpen.value) {
    return 'var(--dealer-error-color)'
  } else if (isAboutToOpen.value || isAboutToClose.value) {
    return 'var(--dealer-accent-color)'
  }
  return 'var(--dealer-success-color)'
})
const statusMessage = computed(() => {
  let message = ''
  if (!open.value && !isAboutToOpen.value) {
    message = closingDetails.value ?? trans('header.opening_hours.status.closed')
  } else if (isAboutToOpen.value && isOpenedIn.value) {
    message = wTrans('header.opening_hours.status.is_about_to_open', {
      time: isOpenedIn.value
    })?.value
  } else if (isAboutToClose.value && isClosedIn.value) {
    message = wTrans('header.opening_hours.status.is_about_to_close', {
      time: isClosedIn.value
    })?.value
  } else {
    message = wTrans('header.opening_hours.status.open', { time: currentOpenRange.end })?.value
  }
  return message
})

watch(lang, (lang, prevLang) => {
  departmentOpeningHours.initialize(lang)
})

onMounted(() => {
  departmentOpeningHours.initialize(lang.value)
  fetchDepartmentStatus()

  intervalId = setInterval(() => {
    fetchDepartmentStatus()
  }, 5000)
})

onUnmounted(() => clearInterval(intervalId))
</script>
<style lang="scss">
.popper-schedules {
  .popper {
    --popper-theme-padding: unset;
  }

  .popper[data-popper-placement^='bottom'] > #arrow {
    left: -2% !important;
  }
}

.show-schedules-container {
  display: flex;
  margin-bottom: -24px !important;

  &--hover {
    border-radius: 15px;
    background-color: rgba(0, 0, 0, 20%);
  }
}

.show-schedules {
  border-radius: 15px;
  font-size: var(--font-size-smallest);
  padding: 0.1em 1em;
}

.v-enter-active,
.v-leave-active {
  transition: all 0.5s ease-in-out;
}

.v-enter-from,
.v-leave-to {
  background-color: transparent;
  opacity: 0;
}
</style>
